import { resolveLocalTaskImportance } from "./resolveLocalTaskImportance";
import {
  RoutineScheduleDaily,
  RoutineScheduleMonthly,
  RoutineScheduleWeekly,
} from "../RoutineEditMain/RoutineEditNavigation";
import { useRoutineEditDirty_Routine$data } from "./__generated__/useRoutineEditDirty_Routine.graphql";

const sortWeekdays = (
  a: { weekday: number; startTime: { hour: number; minute: number } | null },
  b: { weekday: number; startTime: { hour: number; minute: number } | null }
) =>
  a.weekday * 1000 +
  (a.startTime ? a.startTime.hour * 60 + a.startTime.minute : 0) -
  b.weekday * 1000 -
  (b.startTime ? b.startTime.hour * 60 + b.startTime.minute : 0);

function formatTaskImportance(
  taskImportance: { taskId: string; importance: "REQUIRED" | "OPTIONAL" }[]
) {
  return taskImportance
    .sort((a, b) => (a.taskId > b.taskId ? 1 : -1))
    .map((t) => `${t.taskId}${t.importance}`)
    .join(",");
}

export function scheduleDirtyCheck(
  originalSchedule: useRoutineEditDirty_Routine$data["currentSchedule"],
  schedule:
    | RoutineScheduleDaily
    | RoutineScheduleWeekly
    | RoutineScheduleMonthly
    | undefined,
  taskIds: string[]
) {
  if (schedule === undefined) {
    return false;
  }

  const sameFreq =
    (originalSchedule?.__typename === "RoutineScheduleDaily" &&
      schedule?.freq === "daily") ||
    (originalSchedule?.__typename === "RoutineScheduleWeekly" &&
      schedule?.freq === "weekly") ||
    (originalSchedule?.__typename === "RoutineScheduleMonthly" &&
      schedule?.freq === "monthly");
  if (!sameFreq) {
    return true;
  }

  if (
    originalSchedule?.__typename === "RoutineScheduleDaily" &&
    schedule?.freq === "daily"
  ) {
    if (originalSchedule.isAllDay !== schedule.isAllDay) {
      return true;
    }
    if (originalSchedule.deadlineDuration !== schedule.deadlineDuration) {
      return true;
    }

    const originalScheduleWeekdaysKey = originalSchedule.weekdays
      .slice()
      .sort(sortWeekdays)
      .reduce(
        (acc, cur) =>
          `${acc}${cur.weekday}${formatTaskImportance(cur.tasks.slice())}:${
            cur.startTime?.hour ?? 0
          }:${cur.startTime?.minute ?? 0}:`,
        ""
      );
    const localScheduleWeekdaysKey = schedule.weekdays
      .slice()
      .sort(sortWeekdays)
      .reduce(
        (acc, cur) =>
          `${acc}${cur.weekday}${formatTaskImportance(
            resolveLocalTaskImportance(
              taskIds,
              cur.originalTaskImportance,
              cur.taskImportanceChanges
            )
          )}:${cur.startTime?.hour ?? 0}:${cur.startTime?.minute ?? 0}:`,
        ""
      );

    return originalScheduleWeekdaysKey !== localScheduleWeekdaysKey;
  }

  if (
    originalSchedule?.__typename === "RoutineScheduleWeekly" &&
    schedule?.freq === "weekly"
  ) {
    if (originalSchedule.startWeekday !== schedule.startWeekday) {
      return true;
    }
    if (originalSchedule.endWeekday !== schedule.endWeekday) {
      return true;
    }
    if (
      originalSchedule.weeks.slice().sort().join(",") !==
      schedule.weeks.slice().sort().join(",")
    ) {
      return true;
    }

    return false;
  }

  if (
    originalSchedule?.__typename === "RoutineScheduleMonthly" &&
    schedule?.freq === "monthly"
  ) {
    if (originalSchedule.startDay !== schedule.startDay) {
      return true;
    }
    if (originalSchedule.endDay !== schedule.endDay) {
      return true;
    }
    if (
      originalSchedule.months.slice().sort().join(",") !==
      schedule.months.slice().sort().join(",")
    ) {
      return true;
    }

    return false;
  }

  return false;
}
